import React, { useRef, useEffect, useState, useMemo, useCallback } from 'react'
import { useParams, useOutletContext } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragOverlay } from '@dnd-kit/core'
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { token } from '@atlaskit/tokens'
import { HeaderSection } from './components/HeaderSection'
import { TestCaseEditModal } from './components/modals/TestCaseEditModal'
import { TestStepEditModal } from './components/modals/TestStepEditModal'
import { TestScenarioEditModal } from './components/modals/TestScenarioEditModal'
import { ExecutionDetailsModal } from './components/modals/ExecutionDetailsModal'
import { SortableRow, useDragHandle } from './components/SortableRow'
import { ExecutionCell } from './components/ExecutionCell'
import { ScenarioLoadingSkeleton } from './components/common/ScenarioLoadingSkeleton'

/**
 * DRAG HANDLE COMPONENT
 *
 * Renders Atlaskit's DragHandlerIcon (6-dot grid pattern) for drag-and-drop.
 * Uses context from SortableRow to attach dnd-kit listeners.
 * Styled to only appear on row hover (via CSS).
 */
function DragHandle() {
  const context = useDragHandle()
  if (!context) {
    // Fallback when not inside a SortableRow (shouldn't happen in normal use)
    return (
      <span className="drag-handle">
        <DragHandlerIcon label="Drag to reorder" size="small" />
      </span>
    )
  }

  const { listeners, attributes } = context

  // dnd-kit listeners is an object with event handlers, spread them directly
  // We need to wrap the listeners to prevent row's onMouseDown from interfering
  const dragListeners = {
    ...listeners,
    onMouseDown: (e) => {
      e.stopPropagation() // Prevent row's onMouseDown from firing
      if (listeners?.onMouseDown) {
        listeners.onMouseDown(e)
      }
    }
  }

  return (
    <span
      className="drag-handle"
      {...dragListeners}
      {...attributes}
    >
      <DragHandlerIcon label="Drag to reorder" size="small" />
    </span>
  )
}

/**
 * ENTER KEY ICON COMPONENT
 *
 * Custom SVG icon representing the Enter/Return key.
 * Used to indicate "Press Enter to create" action in Create buttons.
 * Designed to match Atlaskit icon styling (16x16 for small size).
 */
function EnterKeyIcon({ size = 'small', label = 'Press Enter' }) {
  const dimensions = size === 'small' ? 16 : 24
  return (
    <span
      role="img"
      aria-label={label}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: dimensions,
        height: dimensions
      }}
    >
      <svg
        width={dimensions}
        height={dimensions}
        viewBox="0 0 16 16"
        fill="none"
      >
        <path
          d="M12 3v6a2 2 0 01-2 2H5m0 0l2.5-2.5M5 11l2.5 2.5"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  )
}

import TextField from '@atlaskit/textfield'
import Button from '@atlaskit/button'
import Lozenge from '@atlaskit/lozenge'
import Avatar from '@atlaskit/avatar'
import Tooltip from '@atlaskit/tooltip'
import { getLabelLozengeAppearance } from './utils/tagColors'
import EditorCloseIcon from '@atlaskit/icon/glyph/editor/close'
import DragHandlerIcon from '@atlaskit/icon/glyph/drag-handler'
import ChevronRightIcon from '@atlaskit/icon/glyph/chevron-right'
import ChevronDownIcon from '@atlaskit/icon/glyph/chevron-down'
import CommentIcon from '@atlaskit/icon/core/comment'
import AttachmentIcon from '@atlaskit/icon/core/attachment'
// Icon used to indicate that a test case has a description, so users
// can see at a glance without opening the test case.
import AlignTextLeftIcon from '@atlaskit/icon/core/align-text-left'
import TrashIcon from '@atlaskit/icon/glyph/trash'
import AddIcon from '@atlaskit/icon/glyph/add'
import CheckCircleIcon from '@atlaskit/icon/glyph/check-circle'
import PersonIcon from '@atlaskit/icon/glyph/person'
import ArrowUpIcon from '@atlaskit/icon/glyph/arrow-up'
import LabelIcon from '@atlaskit/icon/glyph/label'
import LinkIcon from '@atlaskit/icon/glyph/link'
import {
  fetchTestScenarioData,
  toggleTest,
  setExpandedTests,
  toggleStepExpand,
  setExpandedSteps,
  addNewRun,
  createTestCaseAsync,
  createTestStepAsync,
  reorderTestCases,
  reorderTestSteps,
  updateTestCaseAsync,
  updateTestStepAsync,
  deleteTestCaseAsync,
  deleteTestStepAsync,
  selectTestScenario,
  selectTestCases,
  selectTestRuns,
  selectExpandedTests,
  selectExpandedSteps,
  selectLoading,
  selectError,
  selectTestScenarioDetails,
  selectTestScenarioLabels,
  selectProjectLabels,
  fetchProjectLabelsAsync,
  fetchTestScenarioLabelsAsync,
  enrichTestScenarioAssignee,
  fetchTestRunCustomFieldsAsync,
  selectTestRunCustomFields,
  deleteTestRunAsync,
  clearTestRunAsync,
  toggleRunCompletedAsync,
  removeTestRunOptimistic,
  clearRunExecutionsOptimistic,
  toggleRunCompletedOptimistic,
  selectLinkedJiraIssues
} from './store/slices/testScenarioSlice'
import { NewRunModal } from './components/modals/NewRunModal'
import { EditRunModal } from './components/modals/EditRunModal'
import { CustomFieldsModal } from './components/modals/CustomFieldsModal'
import ReportModal from './components/reports/ReportModal'
import { ConfirmDeleteModal } from './components/FolderBrowser/ConfirmDeleteModal'
import { ConfirmRunActionModal } from './components/modals/ConfirmRunActionModal'
import { RunActionsDropdown } from './components/RunActionsDropdown'
import { fetchProjectStatusesAsync } from './store/slices/projectStatusesSlice'
import { getExecutionStatus, formatDate, aggregateRunStatusCounts } from './utils/dataTransformer'
import StatusProgressBar from './components/reports/charts/StatusProgressBar'
import { isJiraConnect, getAllContext } from './utils/jiraContext'
import { getUserDetails, getCachedUserDetails, prefetchUsers } from './utils/userCache'
import { useNotification } from './contexts/NotificationContext'
import { getExpansionState, saveExpansionState } from './utils/expansionStorage'

/**
 * APP COMPONENT
 *
 * Main scenario detail page for viewing and editing test cases/steps.
 * Supports two modes:
 * - Release mode (default): Shows test runs, execution columns, reports
 * - Library mode (isLibrary=true): Template view without runs/reports
 *
 * @param {object} props
 * @param {boolean} props.isLibrary - If true, hides runs/reports (library template mode)
 */
function App({ isLibrary = false }) {
  const dispatch = useDispatch()
  const { scenarioId } = useParams() // Get scenario ID from URL
  const { selectedProject } = useOutletContext() // Get selected project from sidebar
  const scrollContainerRef = useRef(null)
  const stepInputRowRef = useRef(null) // Wrapper for the inline "add step" input row; used to scroll it back into view after Enter-key step creation
  const lastFetchedScenarioRef = useRef(null) // Track last fetched scenario to avoid duplicate fetches
  const prevRunCountRef = useRef(0) // Track previous test-run count so the auto-scroll-right effect can distinguish "user added a run" from the initial 0→N data load
  const { showError, showSuccess } = useNotification() // Notification hooks for feedback

  // Jira context state
  const [jiraContext, setJiraContext] = useState(null)
  
  // Local state for creating test case
  const [isCreating, setIsCreating] = useState(false)
  const [newTestCaseName, setNewTestCaseName] = useState('')
  
  // Local state for adding test steps
  const [addingStepToTestId, setAddingStepToTestId] = useState(null)
  const [newStepText, setNewStepText] = useState('')
  
  // Local state for edit modals
  const [isTestCaseModalOpen, setIsTestCaseModalOpen] = useState(false)
  const [isTestStepModalOpen, setIsTestStepModalOpen] = useState(false)
  const [editingTestCase, setEditingTestCase] = useState(null)
  const [editingTestStep, setEditingTestStep] = useState(null)

  // Execution details modal state
  const [isExecutionDetailsModalOpen, setIsExecutionDetailsModalOpen] = useState(false)
  const [editingExecution, setEditingExecution] = useState(null)

  // Test Scenario edit modal state
  const [isTestScenarioModalOpen, setIsTestScenarioModalOpen] = useState(false)
  const [editingTestScenario, setEditingTestScenario] = useState(null)

  // Drag and drop state
  const [isDragging, setIsDragging] = useState(false)
  const [dragStartPos, setDragStartPos] = useState(null)
  const [pendingClick, setPendingClick] = useState(null)
  
  // Drag state for visual feedback and constraints
  const [dragState, setDragState] = useState({
    isDragging: false,
    draggedItemType: null, // 'testcase' or 'step'
    draggedStepParentId: null, // test case ID if dragging a step
    invalidDropZone: null, // row ID that's an invalid drop zone
    activeId: null, // ID of the currently dragged item
    draggedRowHeight: null // Height of the dragged row to maintain in preview
  })

  // Active item data for DragOverlay card preview (type, title, key/number)
  const [activeItem, setActiveItem] = useState(null)
  
  // DnD sensors with activation delay to distinguish click from drag
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5, // 5px movement threshold before drag activates
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )
  
  // New Run modal state
  const [isNewRunModalOpen, setIsNewRunModalOpen] = useState(false)

  // Report modal state
  const [isReportModalOpen, setIsReportModalOpen] = useState(false)

  // Edit Run modal state - stores the run being edited with pre-fetched user data
  const [editingRun, setEditingRun] = useState(null)
  const [editingRunUserData, setEditingRunUserData] = useState({})

  /**
   * HANDLE EDIT RUN
   * Fetches user data for USER fields before opening the edit modal.
   * Uses cached user details to avoid repeated API calls for the same users.
   * Note: This pre-loads EXISTING user values, not search results (search is dynamic).
   */
  const handleEditRun = async (run) => {
    // Get editable USER fields
    const userFields = testRunCustomFields.filter(
      field => field.showField && field.fieldType === 'USER'
    )

    // Fetch user data for each USER field value using cached version
    const userData = {}
    for (const field of userFields) {
      const accountId = run[field.fieldName]
      if (accountId) {
        try {
          // Use getUserDetails from userCache for efficient caching
          const user = await getUserDetails(accountId)
          if (user) {
            userData[field.fieldName] = {
              label: user.displayName,
              value: accountId, // accountId stays the same
              avatarUrl: user.avatarUrl
            }
          }
        } catch (error) {
          console.error(`Failed to fetch user for ${field.fieldName}:`, error)
        }
      }
    }

    // Set pre-fetched data and open modal
    setEditingRunUserData(userData)
    setEditingRun(run)
  }

  // Custom Fields modal state
  const [isCustomFieldsModalOpen, setIsCustomFieldsModalOpen] = useState(false)

  // Run action confirmation modal state (delete/clear)
  const [runActionModal, setRunActionModal] = useState({
    isOpen: false,
    actionType: null,  // 'delete' or 'clear'
    run: null,
    runNumber: null
  })

  /**
   * HANDLE DELETE RUN
   * Opens confirmation modal for run deletion.
   */
  const handleDeleteRun = (run, runNumber) => {
    setRunActionModal({
      isOpen: true,
      actionType: 'delete',
      run,
      runNumber
    })
  }

  /**
   * HANDLE CLEAR RUN
   * Opens confirmation modal for clearing run executions.
   */
  const handleClearRun = (run, runNumber) => {
    setRunActionModal({
      isOpen: true,
      actionType: 'clear',
      run,
      runNumber
    })
  }

  /**
   * HANDLE TOGGLE RUN COMPLETED
   * Toggles run between completed and active status.
   * Uses optimistic update for immediate UI feedback.
   */
  const handleToggleRunCompleted = async (run, isCompleted) => {
    // Optimistic update - immediately update UI
    dispatch(toggleRunCompletedOptimistic({ testRunId: run.id, isCompleted }))

    try {
      await dispatch(toggleRunCompletedAsync({
        testScenarioId: testScenario.testScenarioId,
        testRunId: run.id,
        isCompleted
      })).unwrap()

      showSuccess(
        isCompleted ? 'Run Completed' : 'Run Activated',
        isCompleted
          ? 'Run has been marked as completed'
          : 'Run has been marked as active'
      )
    } catch (error) {
      // Rollback optimistic update on failure
      dispatch(toggleRunCompletedOptimistic({ testRunId: run.id, isCompleted: !isCompleted }))
      showError('Update Failed', error.message || 'Failed to update run status')
    }
  }

  /**
   * CONFIRM DELETE RUN
   * Called when user confirms deletion in modal.
   * Uses optimistic update with error handling.
   */
  const confirmDeleteRun = async () => {
    const { run } = runActionModal

    // Optimistic update - remove run from UI immediately
    dispatch(removeTestRunOptimistic({ testRunId: run.id }))

    try {
      await dispatch(deleteTestRunAsync({
        testScenarioId: testScenario.testScenarioId,
        testRunId: run.id
      })).unwrap()

      showSuccess('Run Deleted', 'Test run has been permanently deleted')
    } catch (error) {
      // On failure, show error - user may need to refresh page
      showError('Delete Failed', `${error.message || 'Failed to delete run'}. Please refresh the page.`)
      throw error  // Re-throw to keep modal open
    }
  }

  /**
   * CONFIRM CLEAR RUN
   * Called when user confirms clearing in modal.
   * Uses optimistic update with error handling.
   */
  const confirmClearRun = async () => {
    const { run } = runActionModal

    // Optimistic update - clear all executions for this run
    dispatch(clearRunExecutionsOptimistic({ testRunId: run.id }))

    try {
      await dispatch(clearTestRunAsync({
        testScenarioId: testScenario.testScenarioId,
        testRunId: run.id
      })).unwrap()

      showSuccess('Run Cleared', 'All execution results have been cleared')
    } catch (error) {
      // On failure, show error - user may need to refresh page
      showError('Clear Failed', `${error.message || 'Failed to clear run'}. Please refresh the page.`)
      throw error  // Re-throw to keep modal open
    }
  }

  /**
   * CLOSE RUN ACTION MODAL
   */
  const closeRunActionModal = () => {
    setRunActionModal({
      isOpen: false,
      actionType: null,
      run: null,
      runNumber: null
    })
  }

  // State for enriched user data (USER field avatars in run headers)
  // Key: accountId, Value: { displayName, avatarUrl }
  const [enrichedUsers, setEnrichedUsers] = useState({})

  // Delete confirmation modal state
  // Stores the target item to delete: { type: 'testcase'|'step', id, testCaseId?, title }
  const [deleteTarget, setDeleteTarget] = useState(null)

  // Redux state
  const testScenario = useSelector(selectTestScenario)
  const testCases = useSelector(selectTestCases)
  const testRuns = useSelector(selectTestRuns)
  const expandedTests = useSelector(selectExpandedTests)
  const expandedSteps = useSelector(selectExpandedSteps) // Which test steps have expanded details (persisted to localStorage)
  const loading = useSelector(selectLoading)
  const error = useSelector(selectError)
  const testScenarioDetails = useSelector(selectTestScenarioDetails)
  const testScenarioLabels = useSelector(selectTestScenarioLabels)
  const linkedJiraIssues = useSelector(selectLinkedJiraIssues)
  const testRunCustomFields = useSelector(selectTestRunCustomFields)

  /**
   * VISIBLE CUSTOM FIELDS
   *
   * Filters and sorts custom fields for display in header.
   * Only shows fields where showField=true, ordered by orderIndex.
   */
  const visibleCustomFields = useMemo(() => {
    if (!testRunCustomFields || testRunCustomFields.length === 0) return []
    return testRunCustomFields
      .filter(field => field.showField)
      .sort((a, b) => a.orderIndex - b.orderIndex)
  }, [testRunCustomFields])

  // Fetch Jira context on mount (if running in Jira Connect)
  // Note: activeProjectId is managed by AppLayout via sidebar selection — not set here
  useEffect(() => {
    const initializeApp = async () => {
      if (isJiraConnect()) {
        console.log('Running in Jira Connect mode - fetching context...')
        try {
          const context = await getAllContext()
          setJiraContext(context)

          console.log('Jira Context loaded:', context)
        } catch (error) {
          console.warn('Failed to load Jira context:', error)
        }
      } else {
        console.log('Running in standalone mode')
      }
    }

    initializeApp()
  }, [])

  useEffect(() => {
    // Fetch test scenario data when scenarioId changes
    // Also fetch project-level data (statuses, labels, custom fields) on first load
    if (selectedProject?.value && scenarioId && lastFetchedScenarioRef.current !== scenarioId) {
      // Track which scenario we're fetching to avoid duplicate fetches
      lastFetchedScenarioRef.current = scenarioId

      // Always fetch scenario data when scenarioId changes
      dispatch(fetchTestScenarioData(scenarioId))

      // Fetch project-level data only on first load (these don't change per scenario)
      dispatch(fetchProjectStatusesAsync(selectedProject.value))
      dispatch(fetchProjectLabelsAsync(selectedProject.value))
      dispatch(fetchTestRunCustomFieldsAsync(selectedProject.value))
    }
  }, [dispatch, selectedProject, scenarioId])

  // =========================================================================
  // EXPANSION STATE PERSISTENCE (localStorage with LRU eviction)
  // =========================================================================

  // Restore expanded test case and step state from localStorage when scenario changes
  useEffect(() => {
    if (scenarioId) {
      const saved = getExpansionState(scenarioId)
      dispatch(setExpandedTests(saved.testCases))
      dispatch(setExpandedSteps(saved.testSteps))
    }
  }, [scenarioId, dispatch])

  // Save expanded test case state to localStorage whenever it changes
  useEffect(() => {
    if (scenarioId) {
      saveExpansionState(scenarioId, expandedTests, expandedSteps)
    }
  }, [scenarioId, expandedTests, expandedSteps])

  // Fetch scenario labels when scenario ID is available
  useEffect(() => {
    if (testScenario.testScenarioId) {
      dispatch(fetchTestScenarioLabelsAsync(testScenario.testScenarioId))
    }
  }, [dispatch, testScenario.testScenarioId])

  /**
   * FETCH ASSIGNEE DETAILS FROM JIRA
   *
   * When scenario loads with accountId-only assignee, fetch full user details
   * from Jira REST API and update Redux state.
   * Uses cached user details to avoid repeated API calls for the same users.
   *
   * In Jira Connect mode: fetches from Jira REST API (with caching).
   * In dev/standalone mode: falls back to localStorage cache lookup.
   */
  useEffect(() => {
    const enrichAssigneeDetails = async () => {
      // Check if we have an assignee with accountId but missing displayName
      const assignee = testScenarioDetails?.testScenarioAssignee
      if (assignee?.accountId && !assignee?.displayName) {
        if (isJiraConnect()) {
          // Jira Connect mode: fetch from Jira REST API with caching
          try {
            const userDetails = await getUserDetails(assignee.accountId)
            if (userDetails) {
              dispatch(enrichTestScenarioAssignee(userDetails))
            }
          } catch (error) {
            console.warn('Failed to fetch assignee details from Jira:', error)
            // Graceful degradation - UI will show fallback text
          }
        } else {
          // Dev/standalone mode: try cache-only lookup from localStorage
          const cached = getCachedUserDetails(assignee.accountId)
          if (cached) {
            dispatch(enrichTestScenarioAssignee(cached))
          }
        }
      }
    }

    enrichAssigneeDetails()
  }, [dispatch, testScenarioDetails?.testScenarioAssignee?.accountId, testScenarioDetails?.testScenarioAssignee?.displayName])

  /**
   * ENRICH USER FIELDS IN TEST RUNS
   *
   * For USER type custom fields, fetch user details from Jira API.
   * Uses prefetchUsers from userCache for efficient bulk caching.
   * This enables displaying proper avatars and display names in run headers.
   * Cached users persist across page loads (14-day localStorage cache).
   */
  useEffect(() => {
    const enrichUserFields = async () => {
      if (!isJiraConnect()) return
      if (!testRuns || testRuns.length === 0) return
      if (!visibleCustomFields || visibleCustomFields.length === 0) return

      // Find USER type fields
      const userFields = visibleCustomFields.filter(f => f.fieldType === 'USER')
      if (userFields.length === 0) return

      // Collect all unique accountIds that need enrichment
      const accountIdsToFetch = []
      testRuns.forEach(run => {
        userFields.forEach(field => {
          const accountId = run[field.fieldName]
          if (accountId && !enrichedUsers[accountId]) {
            accountIdsToFetch.push(accountId)
          }
        })
      })

      const uniqueIds = [...new Set(accountIdsToFetch)]
      if (uniqueIds.length === 0) return

      try {
        // Use prefetchUsers from userCache for efficient bulk caching
        // This handles deduplication and localStorage persistence automatically
        await prefetchUsers(uniqueIds)

        // Build enriched users map from cache after prefetch completes
        const newEnrichedUsers = { ...enrichedUsers }
        uniqueIds.forEach(accountId => {
          const cached = getCachedUserDetails(accountId)
          if (cached) {
            newEnrichedUsers[accountId] = {
              displayName: cached.displayName,
              avatarUrl: cached.avatarUrl
            }
          }
        })

        setEnrichedUsers(newEnrichedUsers)
      } catch (error) {
        console.warn('Failed to fetch user details:', error)
      }
    }

    enrichUserFields()
  }, [testRuns, visibleCustomFields, enrichedUsers])

  useEffect(() => {
    // Auto-scroll right only when a new run is added (length grew after data was loaded).
    // Skipping the initial 0→N data-load transition keeps the page booted at scrollLeft=0
    // so the bottom-left "+ Create" row is in view. Without the prev > 0 guard, the page
    // would boot scrolled all the way right and hide the create row off-screen to the left.
    if (
      scrollContainerRef.current &&
      testRuns.length > prevRunCountRef.current &&
      prevRunCountRef.current > 0
    ) {
      scrollContainerRef.current.scrollLeft = scrollContainerRef.current.scrollWidth
    }
    prevRunCountRef.current = testRuns.length
  }, [testRuns.length])

  // Track if drag started to prevent click-to-edit
  const handleDragStart = (event) => {
    setIsDragging(true)
    setPendingClick(null)
    
    const activeId = event.active.id.toString()
    
    // Capture the original row height
    const activeElement = document.querySelector(`[data-row-id="${activeId}"]`)
    const rowHeight = activeElement ? activeElement.offsetHeight : null
    
    // Track drag state for visual feedback and populate overlay card data
    if (activeId.startsWith('step-')) {
      const match = activeId.match(/^step-(.+?)-(.+)$/)
      if (match) {
        const [, testCaseId, stepId] = match
        setDragState({
          isDragging: true,
          draggedItemType: 'step',
          draggedStepParentId: testCaseId,
          invalidDropZone: null,
          activeId: activeId,
          draggedRowHeight: rowHeight
        })
        // Find the step data for the overlay card
        const parentCase = testCases.find(tc => tc.id === testCaseId || tc.id === Number(testCaseId))
        const step = parentCase?.testSteps?.find(s => s.id === stepId || s.id === Number(stepId))
        if (step) {
          setActiveItem({ type: 'step', title: step.title, key: step.number })
        }
      }
    } else if (activeId.startsWith('testcase-')) {
      const testCaseId = activeId.replace('testcase-', '')
      setDragState({
        isDragging: true,
        draggedItemType: 'testcase',
        draggedStepParentId: null,
        invalidDropZone: null,
        activeId: activeId,
        draggedRowHeight: rowHeight
      })
      // Find the test case data for the overlay card
      const testCase = testCases.find(tc => tc.id === testCaseId || tc.id === Number(testCaseId))
      if (testCase) {
        setActiveItem({ type: 'testcase', title: testCase.title, key: testCase.testCaseKey || testCase.number })
      }
    }
  }

  // Handle mouse down - start tracking for click vs drag
  const handleMouseDown = (e, item, isStep = false) => {
    setDragStartPos({ x: e.clientX, y: e.clientY })
    setIsDragging(false)
    setPendingClick({ item, isStep, event: e })
  }

  // Handle mouse up - open dialog if no drag occurred
  useEffect(() => {
    if (!dragStartPos) {
      return () => {} // Always return a cleanup function, even if no-op
    }

    const handleMouseUp = (e) => {
      if (!isDragging && pendingClick && dragStartPos) {
        // Calculate distance moved between mousedown and mouseup
        // This distinguishes between a click (< 5px) and text selection drag (>= 5px)
        const dx = e.clientX - dragStartPos.x
        const dy = e.clientY - dragStartPos.y
        const distance = Math.sqrt(dx * dx + dy * dy)

        // Only open modal if mouse moved less than 5px (was a click, not a drag/selection)
        if (distance < 5) {
          const { item, isStep } = pendingClick
          if (isStep) {
            setEditingTestStep({ ...item.step, testCaseId: item.testCase.id })
            setIsTestStepModalOpen(true)
          } else {
            setEditingTestCase(item)
            setIsTestCaseModalOpen(true)
          }
        }
      }
      // Clean up state regardless of whether modal was opened
      setDragStartPos(null)
      setIsDragging(false)
      setPendingClick(null)
    }

    document.addEventListener('mouseup', handleMouseUp)
    return () => {
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [dragStartPos, isDragging, pendingClick])

  /**
   * HANDLE EXECUTION CELL CLICK
   *
   * Opens modal to view/edit execution details.
   * Called when user clicks on an execution cell that has a status.
   */
  const handleExecutionClick = (e, executionData) => {
    setEditingExecution(executionData)
    setIsExecutionDetailsModalOpen(true)
  }

  /**
   * HANDLE TEST SCENARIO CLICK
   *
   * Opens modal to edit test scenario name, description, and priority
   * Triggered by clicking on title or description in header
   */
  const handleTestScenarioClick = (e) => {
    e.stopPropagation()
    setEditingTestScenario(testScenarioDetails)
    setIsTestScenarioModalOpen(true)
  }

  // Handle drag over to detect invalid drop zones
  const handleDragOver = (event) => {
    const { active, over } = event
    
    if (!over) {
      setDragState(prev => ({ ...prev, invalidDropZone: null }))
      return
    }
    
    const activeId = active.id.toString()
    const overId = over.id.toString()
    
    // Prevent dropping on header or create row (no visual feedback, just ignore)
    if (overId === 'header' || overId === 'create-row') {
      setDragState(prev => ({ ...prev, invalidDropZone: null }))
      return
    }
    
    // If dragging a test step
    if (activeId.startsWith('step-')) {
      const activeMatch = activeId.match(/^step-(.+?)-(.+)$/)
      if (activeMatch) {
        const [, activeTestCaseId] = activeMatch
        
        // Check if over a test step from a different test case
        if (overId.startsWith('step-')) {
          const overMatch = overId.match(/^step-(.+?)-(.+)$/)
          if (overMatch) {
            const [, overTestCaseId] = overMatch
            if (activeTestCaseId !== overTestCaseId) {
              setDragState(prev => ({ 
                ...prev, 
                invalidDropZone: overId 
              }))
              return
            }
          }
        }
        
        // Check if over a test case (not allowed)
        if (overId.startsWith('testcase-')) {
          const overTestCaseId = overId.replace('testcase-', '')
          if (activeTestCaseId !== overTestCaseId) {
            setDragState(prev => ({ 
              ...prev, 
              invalidDropZone: overId 
            }))
            return
          }
        }
      }
    }
    
    // Clear invalid drop zone if valid
    setDragState(prev => ({ ...prev, invalidDropZone: null }))
  }

  // Handle drag end for test cases
  const handleDragEnd = (event) => {
    const { active, over } = event
    
    console.log('Drag end:', { active: active?.id, over: over?.id })
    
    // Reset drag state and clear overlay card
    setActiveItem(null)
    setDragState({
      isDragging: false,
      draggedItemType: null,
      draggedStepParentId: null,
      invalidDropZone: null,
      activeId: null,
      draggedRowHeight: null
    })
    
    if (!over || active.id === over.id) {
      console.log('No valid drop target or same position')
      return
    }

    // Check if dragging test case or test step
    const activeId = active.id.toString()
    const overId = over.id.toString()
    
    // Prevent dropping on header or create row
    if (overId === 'header' || overId === 'create-row') {
      console.log('Cannot drop on header or create row')
      return
    }
    
    console.log('Processing drag:', { activeId, overId })
    
    // Test case drag (format: "testcase-{id}")
    if (activeId.startsWith('testcase-') && overId.startsWith('testcase-')) {
      const activeTestCaseId = activeId.replace('testcase-', '')
      const overTestCaseId = overId.replace('testcase-', '')
      
      console.log('Test case drag:', { activeTestCaseId, overTestCaseId })
      console.log('Test cases in array:', testCases.map(tc => ({ id: tc.id, idType: typeof tc.id })))
      
      // Convert to number for comparison (IDs might be numbers from API)
      const activeIdNum = Number(activeTestCaseId)
      const overIdNum = Number(overTestCaseId)
      
      // Calculate new orderIndex BEFORE optimistic update
      // Try both string and number comparison
      const activeIndex = testCases.findIndex(tc => tc.id === activeTestCaseId || tc.id === activeIdNum)
      const overIndex = testCases.findIndex(tc => tc.id === overTestCaseId || tc.id === overIdNum)
      
      if (activeIndex === -1 || overIndex === -1) {
        console.log('Could not find test cases in array', { activeIndex, overIndex, activeIdNum, overIdNum })
        return
      }
      
      // Calculate the new orderIndex based on where we're dropping
      // The overIndex represents the position we're dropping onto
      // We want the dragged item to be at overIndex + 1 (1-based indexing)
      const newOrderIndex = overIndex + 1
      
      console.log('Updating test case orderIndex:', { activeTestCaseId, newOrderIndex, activeIndex, overIndex })
      
      // Optimistically update state (use the actual ID from the array, not the string)
      const actualActiveId = testCases[activeIndex].id
      const actualOverId = testCases[overIndex].id
      dispatch(reorderTestCases({ activeId: String(actualActiveId), overId: String(actualOverId) }))
      
      // Update via API (use the actual ID from the array)
      dispatch(updateTestCaseAsync({
        testCaseId: String(actualActiveId),
        updates: { orderIndex: newOrderIndex }
      }))
    }
    
    // Test step drag (format: "step-{testCaseId}-{stepId}")
    if (activeId.startsWith('step-') && overId.startsWith('step-')) {
      // Extract testCaseId and stepId from format "step-{testCaseId}-{stepId}"
      const activeMatch = activeId.match(/^step-(.+?)-(.+)$/)
      const overMatch = overId.match(/^step-(.+?)-(.+)$/)
      
      if (activeMatch && overMatch) {
        const [, activeTestCaseId, activeStepId] = activeMatch
        const [, overTestCaseId, overStepId] = overMatch
        
        console.log('Test step drag:', { activeTestCaseId, activeStepId, overTestCaseId, overStepId })
        
        // Check if same test case
        if (activeTestCaseId === overTestCaseId) {
          const testCaseId = activeTestCaseId
          // Try both string and number comparison for test case ID
          const testCase = testCases.find(tc => tc.id === testCaseId || tc.id === Number(testCaseId))
          
          if (testCase && testCase.testSteps) {
            // Convert step IDs to numbers for comparison
            const activeStepIdNum = Number(activeStepId)
            const overStepIdNum = Number(overStepId)
            
            // Calculate new orderIndex BEFORE optimistic update
            // Try both string and number comparison
            const activeStepIndex = testCase.testSteps.findIndex(s => s.id === activeStepId || s.id === activeStepIdNum)
            const overStepIndex = testCase.testSteps.findIndex(s => s.id === overStepId || s.id === overStepIdNum)
            
            if (activeStepIndex === -1 || overStepIndex === -1) {
              console.log('Could not find test steps in array', { activeStepIndex, overStepIndex, activeStepId, overStepId })
              return
            }
            
            const newOrderIndex = overStepIndex + 1
            
            console.log('Updating test step orderIndex:', { testCaseId, activeStepId, newOrderIndex })
            
            // Optimistically update state (use actual IDs from array)
            const actualActiveStepId = testCase.testSteps[activeStepIndex].id
            const actualOverStepId = testCase.testSteps[overStepIndex].id
            dispatch(reorderTestSteps({ 
              testCaseId: String(testCase.id), 
              activeId: String(actualActiveStepId), 
              overId: String(actualOverStepId)
            }))
            
            // Update via API (use actual ID from array)
            dispatch(updateTestStepAsync({
              testCaseId: String(testCase.id),
              testStepId: String(actualActiveStepId),
              updates: { orderIndex: newOrderIndex }
            }))
          }
        } else {
          console.log('Test steps belong to different test cases, ignoring')
          // Visual feedback: the drag was invalid, item will snap back
          // The state is already reset above, so the visual feedback will clear
        }
      }
    }
  }
  
  // Handle drag cancel (when drag is cancelled)
  const handleDragCancel = () => {
    // Clear overlay card and reset drag state
    setActiveItem(null)
    setDragState({
      isDragging: false,
      draggedItemType: null,
      draggedStepParentId: null,
      invalidDropZone: null,
      activeId: null,
      draggedRowHeight: null
    })
  }

  const handleCloseTestCaseModal = () => {
    setIsTestCaseModalOpen(false)
    setEditingTestCase(null)
  }

  const handleCloseTestStepModal = () => {
    setIsTestStepModalOpen(false)
    setEditingTestStep(null)
  }

  const handleCloseExecutionDetailsModal = () => {
    setIsExecutionDetailsModalOpen(false)
    setEditingExecution(null)
  }

  /**
   * CLOSE TEST SCENARIO MODAL
   */
  const handleCloseTestScenarioModal = () => {
    setIsTestScenarioModalOpen(false)
    setEditingTestScenario(null)
  }

  /**
   * TOGGLE STEP EXPANSION
   *
   * Toggles the expand/collapse state for a single test step.
   * Uses Set for O(1) operations.
   */
  /**
   * TOGGLE STEP EXPANSION
   * Dispatches to Redux (which syncs to localStorage via useEffect).
   */
  const toggleStepExpansion = useCallback((stepId) => {
    dispatch(toggleStepExpand(stepId))
  }, [dispatch])

  /**
   * HANDLE DELETE CLICK
   *
   * Opens delete confirmation modal for a test case or step.
   * Stores target item info in deleteTarget state.
   */
  const handleDeleteClick = useCallback((type, item) => {
    if (type === 'testcase') {
      setDeleteTarget({ type: 'testcase', id: item.id, title: item.title })
    } else {
      setDeleteTarget({
        type: 'step',
        id: item.step.id,
        testCaseId: item.testCase.id,
        title: item.step.title
      })
    }
  }, [])

  /**
   * HANDLE CONFIRM DELETE
   *
   * Dispatches delete action after user confirms in modal.
   * Handles both test case and test step deletion.
   */
  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTarget) return
    try {
      if (deleteTarget.type === 'testcase') {
        await dispatch(deleteTestCaseAsync({ testCaseId: deleteTarget.id })).unwrap()
      } else {
        await dispatch(deleteTestStepAsync({
          testCaseId: deleteTarget.testCaseId,
          testStepId: deleteTarget.id
        })).unwrap()
      }
      setDeleteTarget(null)
    } catch (error) {
      console.error('Delete failed:', error)
    }
  }, [dispatch, deleteTarget])

  // Show loading state - skeleton placeholder mimicking the real page layout
  // Also show skeleton when data hasn't loaded yet (initial state: loading=false, testScenarioId=null)
  // This prevents a flash of empty content before the useEffect dispatch sets loading=true
  if (loading || (!testScenario.testScenarioId && !error)) {
    return <ScenarioLoadingSkeleton />
  }

  // Show error state
  if (error) {
    return (
      <div className="test-management-container">
        <div style={{ padding: '20px', textAlign: 'center', color: token('color.text.danger') }}>
          <h2>Error loading test scenario</h2>
          <p>{error}</p>
          <button onClick={() => dispatch(fetchTestScenarioData(scenarioId))}>
            Retry
          </button>
        </div>
      </div>
    )
  }

  const handleToggleTest = (testId) => {
    dispatch(toggleTest(testId))
  }

  /**
   * HANDLE ADD NEW RUN
   *
   * Opens the NewRunModal for creating a test run with custom field values.
   * The modal dynamically renders fields based on testRunCustomFields from API.
   */
  const handleAddNewRun = () => {
    setIsNewRunModalOpen(true)
  }

  /**
   * HANDLE CLOSE NEW RUN MODAL
   */
  const handleCloseNewRunModal = () => {
    setIsNewRunModalOpen(false)
  }

  /**
   * HANDLE OPEN REPORT MODAL
   *
   * Opens the ReportModal for viewing execution summary reports.
   * Initially opens at scenario level with current scenario context.
   */
  const handleOpenReport = () => {
    setIsReportModalOpen(true)
  }

  /**
   * HANDLE CLOSE REPORT MODAL
   */
  const handleCloseReportModal = () => {
    setIsReportModalOpen(false)
  }

  const handleCreateTestCase = async () => {
    if (!newTestCaseName.trim()) return

    await dispatch(createTestCaseAsync({
      testScenarioId: scenarioId,
      testCaseName: newTestCaseName,
      jiraProjectId: selectedProject?.value,
      description: '',
      priority: 'Medium'
    }))

    // Clear input but keep create mode active for continuous creation
    setNewTestCaseName('')
    // isCreating stays true - only cancel button will close it

    // Scroll to the newly created test case after React re-renders
    requestAnimationFrame(() => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTo({
          top: scrollContainerRef.current.scrollHeight,
          behavior: 'smooth'
        })
      }
    })
  }

  const handleCreateTestStep = async (testCaseId) => {
    if (!newStepText.trim()) return
    
    await dispatch(createTestStepAsync({
      testCaseId,
      stepAction: newStepText
    }))
    
    // Clear input but keep adding mode active for continuous step creation
    setNewStepText('')
  }

  const handleCancelStep = () => {
    setAddingStepToTestId(null)
    setNewStepText('')
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleCreateTestCase()
    } else if (e.key === 'Escape') {
      setIsCreating(false)
      setNewTestCaseName('')
    }
  }

  const handleStepKeyPress = async (e, testCaseId) => {
    if (e.key === 'Enter') {
      // Wait for the step to be created and React to commit the new row, then
      // scroll the input back into view. We can't use scrollIntoView here because
      // the .sticky-create-container at the bottom of .execution-scroll-container
      // visually overlays the bottom of the viewport — scrollIntoView would park
      // the input directly behind it. Instead, compute the visible bottom edge
      // (subtracting the sticky footer's height) and scroll just enough to lift
      // the input row above it, with a small buffer for breathing room.
      await handleCreateTestStep(testCaseId)
      requestAnimationFrame(() => {
        const container = scrollContainerRef.current
        const row = stepInputRowRef.current
        if (!container || !row) return
        const sticky = container.querySelector('.sticky-create-container')
        const stickyHeight = sticky ? sticky.offsetHeight : 0
        const containerRect = container.getBoundingClientRect()
        const rowRect = row.getBoundingClientRect()
        const visibleBottom = containerRect.bottom - stickyHeight
        const overflow = rowRect.bottom - visibleBottom
        if (overflow > 0) {
          container.scrollBy({ top: overflow + 8, behavior: 'smooth' })
        }
      })
    } else if (e.key === 'Escape') {
      handleCancelStep()
    }
  }

  const handleTestCaseBlur = () => {
    if (newTestCaseName.trim()) {
      handleCreateTestCase()
    }
    // Exit creation mode on blur
    setIsCreating(false)
  }

  const handleStepBlur = (testCaseId) => {
    if (newStepText.trim()) {
      handleCreateTestStep(testCaseId)
    }
    // Exit adding mode on blur
    setAddingStepToTestId(null)
  }

  const handleCancel = () => {
    setIsCreating(false)
    setNewTestCaseName('')
  }

  const getTagClass = (tag) => {
    const tagClasses = {
      'High': 'tag-priority-high',
      'Medium': 'tag-priority-medium',
      'Low': 'tag-priority-low'
    }
    return tagClasses[tag] || 'tag-category'
  }

  /**
   * GET PRIORITY LOZENGE APPEARANCE
   *
   * Maps priority level to Atlaskit Lozenge appearance prop
   * Follows Jira's priority color scheme
   */
  const getPriorityAppearance = (priority) => {
    const priorityMap = {
      'Low': 'default',      // Gray
      'Medium': 'inprogress', // Blue
      'High': 'moved',        // Yellow/Orange
      'Critical': 'removed'   // Red
    }
    return priorityMap[priority] || 'default'
  }

  /**
   * RENDER CUSTOM FIELD VALUE
   *
   * Renders a test run field value based on field type.
   * - TIMESTAMP: Formatted date string
   * - TEXT: Plain text value
   * - USER: Avatar with user details (enriched separately)
   *
   * @param {object} run - The test run object
   * @param {object} field - The custom field definition
   * @returns {JSX.Element} - Rendered field value
   */
  const renderCustomFieldValue = (run, field) => {
    const value = run[field.fieldName]

    switch (field.fieldType) {
      case 'TIMESTAMP':
        return (
          <div className="detail-value">
            {value ? formatDate(value) : '-'}
          </div>
        )

      case 'USER':
        // USER fields contain accountId string
        // Use enriched user data for avatar and display name
        if (value) {
          const enrichedUser = enrichedUsers[value]
          const displayName = enrichedUser?.displayName || value
          const avatarUrl = enrichedUser?.avatarUrl

          return (
            <Tooltip content={displayName}>
              {/* Use detail-value class for consistent font styling with other run header fields */}
              <div className="detail-value" style={{ display: 'inline-flex', alignItems: 'center', height: '20px' }}>
                <Avatar
                  size="xsmall"
                  src={avatarUrl}
                  name={displayName}
                />
                <span style={{
                  marginLeft: '6px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {displayName}
                </span>
              </div>
            </Tooltip>
          )
        }
        return <div className="detail-value" style={{ height: '20px', display: 'flex', alignItems: 'center' }}>-</div>

      case 'TEXT':
      default:
        // Wrap in Tooltip to show full text on hover when truncated
        return value ? (
          <Tooltip content={value}>
            <div className="detail-value">
              {value}
            </div>
          </Tooltip>
        ) : (
          <div className="detail-value">-</div>
        )
    }
  }

  // Create table rows
  const tableRows = []
  
  // Header row
  tableRows.push({
    id: 'header',
    draftingContent: (
      /* Header layout: scenario-header (left) and clickable-field-area (right) as siblings
       * This separation ensures labels align horizontally with execution column values
       * by keeping clickable-field-area outside scenario-header's padding context */
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'stretch', height: '100%' }}>
        {/* LEFT SECTION: Scenario metadata (title, description, assignee, priority, labels) */}
        <div className="scenario-header" style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          {/* TOP SECTION: Title + Description */}
          <div>
            {/* CLICKABLE TITLE - Opens edit modal */}
            {/* Scenario key now displayed in breadcrumb (HeaderSection), not here */}
            <h2
              className="scenario-title clickable"
              onClick={handleTestScenarioClick}
              style={{ cursor: 'pointer' }}
            >
              {testScenario.testScenarioName}
            </h2>

            {/* CLICKABLE DESCRIPTION - Opens edit modal, uses dynamic data from API */}
            {/* Hidden entirely when description is empty; title and metadata bar still open the edit modal */}
            {testScenario.testScenarioDescription && (
              <p
                className="scenario-description clickable"
                onClick={handleTestScenarioClick}
                style={{ cursor: 'pointer' }}
              >
                {testScenario.testScenarioDescription}
              </p>
            )}
          </div>

          {/* BOTTOM SECTION: Jira-style labeled metadata bar */}
          {/* Each field: icon + label + value, separated by vertical dividers */}
          {/* Entire bar is clickable to open the edit modal */}
          <div
            className="scenario-metadata-bar"
            onClick={handleTestScenarioClick}
            style={{
              display: 'flex',
              alignItems: 'center',
              paddingTop: '10px',
              paddingLeft: '4px',
              cursor: 'pointer',
              flexWrap: 'wrap',
              gap: '0'
            }}
          >
            {/* ASSIGNEE FIELD - only shown when an assignee is assigned */}
            {testScenario.testScenarioAssignee?.displayName && (
              <>
                <div className="scenario-metadata-field" style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '4px 12px 4px 0'
                }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                    <Avatar
                      size="xsmall"
                      src={testScenario.testScenarioAssignee?.avatarUrl || undefined}
                      name={testScenario.testScenarioAssignee.displayName}
                    />
                    <span style={{
                      fontSize: '12px',
                      fontWeight: 500,
                      color: 'var(--ds-text, #172B4D)',
                      maxWidth: '120px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {testScenario.testScenarioAssignee.displayName}
                    </span>
                  </span>
                </div>

                {/* VERTICAL DIVIDER between assignee and priority */}
                <div style={{
                  width: '1px',
                  height: '16px',
                  backgroundColor: 'var(--ds-border, #DFE1E6)',
                  flexShrink: 0,
                  margin: '0 4px'
                }} />
              </>
            )}

            {/* PRIORITY FIELD - ArrowUpIcon + label + lozenge */}
            <div className="scenario-metadata-field" style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '4px 12px 4px 8px'
            }}>
              <ArrowUpIcon
                label=""
                size="small"
                primaryColor="var(--ds-icon-subtle, #6B778C)"
              />
              <span style={{
                fontSize: '11px',
                fontWeight: 600,
                color: 'var(--ds-text-subtlest, #6B778C)',
                letterSpacing: '0.02em',
                whiteSpace: 'nowrap'
              }}>
                Priority
              </span>
              {testScenario.testScenarioPriority ? (
                <Lozenge appearance={getPriorityAppearance(testScenario.testScenarioPriority)}>
                  {testScenario.testScenarioPriority}
                </Lozenge>
              ) : (
                <span style={{
                  fontSize: '12px',
                  color: 'var(--ds-text-subtlest, #6B778C)',
                  fontStyle: 'italic'
                }}>
                  None
                </span>
              )}
            </div>

            {/* LABELS FIELD - Only shown when labels exist */}
            {testScenarioLabels && testScenarioLabels.length > 0 && (
              <>
                {/* VERTICAL DIVIDER between priority and labels */}
                <div style={{
                  width: '1px',
                  height: '16px',
                  backgroundColor: 'var(--ds-border, #DFE1E6)',
                  flexShrink: 0,
                  margin: '0 4px'
                }} />

                <div className="scenario-metadata-field" style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '4px 0 4px 8px'
                }}>
                  <LabelIcon
                    label=""
                    size="small"
                    primaryColor="var(--ds-icon-subtle, #6B778C)"
                  />
                  <span style={{
                    fontSize: '11px',
                    fontWeight: 600,
                    color: 'var(--ds-text-subtlest, #6B778C)',
                    letterSpacing: '0.02em',
                    whiteSpace: 'nowrap'
                  }}>
                    Labels
                  </span>
                  <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', alignItems: 'center' }}>
                    {testScenarioLabels.map((label) => (
                      <Lozenge key={label.id} appearance={getLabelLozengeAppearance(label.name)}>
                        {label.name}
                      </Lozenge>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* LINKED JIRA ISSUES FIELD - Only shown when linked issues exist */}
            {linkedJiraIssues && linkedJiraIssues.length > 0 && (
              <>
                {/* VERTICAL DIVIDER */}
                <div style={{
                  width: '1px',
                  height: '16px',
                  backgroundColor: 'var(--ds-border, #DFE1E6)',
                  flexShrink: 0,
                  margin: '0 4px'
                }} />

                <div className="scenario-metadata-field" style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '4px 0 4px 8px'
                }}>
                  <LinkIcon
                    label=""
                    size="small"
                    primaryColor="var(--ds-icon-subtle, #6B778C)"
                  />
                  <span style={{
                    fontSize: '11px',
                    fontWeight: 600,
                    color: 'var(--ds-text-subtlest, #6B778C)',
                    letterSpacing: '0.02em',
                    whiteSpace: 'nowrap'
                  }}>
                    Issues
                  </span>
                  <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                    {linkedJiraIssues.map((link) => {
                      /* Build Jira browse URL from xdm_e query param (Jira Connect iframe base URL) */
                      const jiraBaseUrl = new URLSearchParams(window.location.search).get('xdm_e')
                      const issueUrl = jiraBaseUrl ? `${jiraBaseUrl}/browse/${link.jiraIssueKey}` : null
                      return issueUrl ? (
                        <a
                          key={link.id}
                          href={issueUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          style={{ textDecoration: 'none' }}
                        >
                          <Lozenge appearance="new">
                            {link.jiraIssueKey}
                          </Lozenge>
                        </a>
                      ) : (
                        <Lozenge key={link.id} appearance="new">
                          {link.jiraIssueKey}
                        </Lozenge>
                      )
                    })}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
        {/* RIGHT SECTION: Dynamic custom field labels - Now outside scenario-header for independent styling */}
        {/* Only shown in release mode, hidden for library templates */}
        {!isLibrary && (
          <div
            className="clickable-field-area header-field-labels"
            onClick={() => setIsCustomFieldsModalOpen(true)}
            title="Click to manage execution fields"
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'flex-end', gap: '4px', height: '100%' }}
          >
            {/* Static Run # label - clicks bubble to parent to open manage execution fields dialog */}
            <div
              className="detail-label"
              style={{ height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}
            >
              Run #:
            </div>
            {/* Render dynamic labels from custom fields API - no fallback to avoid race condition flash */}
            {visibleCustomFields.length > 0 && (
              visibleCustomFields.map(field => (
                <div
                  key={field.id}
                  className="detail-label"
                  style={{ height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}
                >
                  {field.fieldName}:
                </div>
              ))
            )}
          </div>
        )}
      </div>
    ),
    runContents: isLibrary ? [] : testRuns.map((run, index) => (
      <div key={run.id} className={`execution-header-wrapper ${run.isCompleted ? 'run-completed' : ''}`}>
        {/* Run field values */}
        <div className="execution-header" style={{ display: 'flex', flexDirection: 'column', gap: '4px', justifyContent: 'flex-end', height: '100%' }}>
          {/* Static Run # value - with actions dropdown and completed indicator */}
          <div
            className="detail-value run-header-row"
            style={{ height: '24px', display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'space-between' }}
          >
            {/* Left side: Run number and completed indicator */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              {String(index + 1).padStart(2, '0')}
              {/* Completed checkmark indicator - shows when run.isCompleted is true */}
              {run.isCompleted && (
                <Tooltip content="This run is completed">
                  <span style={{ display: 'flex', alignItems: 'center' }}>
                    <CheckCircleIcon
                      label="Run completed"
                      size="small"
                      primaryColor={token('color.icon.success', '#22A06B')}
                    />
                  </span>
                </Tooltip>
              )}
            </div>

            {/* Right side: Actions dropdown menu - shown on hover */}
            <div
              onClick={(e) => e.stopPropagation()}
              className="run-actions-dropdown"
            >
              <RunActionsDropdown
                run={run}
                runNumber={index + 1}
                onEdit={() => handleEditRun(run)}
                onToggleCompleted={(isCompleted) => handleToggleRunCompleted(run, isCompleted)}
                onClear={() => handleClearRun(run, index + 1)}
                onDelete={() => handleDeleteRun(run, index + 1)}
              />
            </div>
          </div>
          {/* Render dynamic values from custom fields API - no fallback to avoid race condition flash */}
          {visibleCustomFields.length > 0 && (
            visibleCustomFields.map(field => (
              <div
                key={field.id}
                className="field-value-clickable"
                style={{ height: '24px', display: 'flex', alignItems: 'center' }}
                onClick={() => handleEditRun(run)}
              >
                {renderCustomFieldValue(run, field)}
              </div>
            ))
          )}
          {/* STATUS DISTRIBUTION BAR - Shows execution status breakdown for this run */}
          <div className="execution-header-status-bar">
            <StatusProgressBar
              statusDistribution={aggregateRunStatusCounts(testCases, run.id)}
              height={6}
              width="100%"
              showTooltip={true}
            />
          </div>
        </div>
      </div>
    ))
  })

  // Test case and step rows
  testCases.forEach((item) => {
    if (item.type === 'test') {
      // Determine if this test case is collapsed (not expanded or has no steps)
      const isExpanded = expandedTests.includes(item.id)
      const hasSteps = item.testSteps && item.testSteps.length > 0
      const isCollapsedTestcase = !isExpanded || !hasSteps

      // Test case row
      tableRows.push({
        id: `testcase-${item.id}`,
        type: 'testcase',
        item: item,
        isCollapsedTestcase: isCollapsedTestcase,
        draftingContent: (
          <div 
            className="test-group"
            onMouseDown={(e) => {
              // Don't open modal if clicking on execution columns
              if (e.target.closest('.execution-column') || e.target.closest('.execution-cell')) {
                return
              }
              // Only open modal if not clicking on expand icon, add button, delete button, or drag handle
              if (!e.target.closest('.expand-icon') &&
                  !e.target.closest('.add-step-button') &&
                  !e.target.closest('.row-delete-button') &&
                  !e.target.closest('.drag-handle')) {
                e.stopPropagation()
                handleMouseDown(e, item, false)
              }
            }}
            onClick={(e) => {
              // Prevent click event from bubbling when clicking drag handle
              if (e.target.closest('.drag-handle')) {
                e.stopPropagation()
              }
            }}
            style={{ cursor: 'pointer', position: 'relative', display: 'flex', alignItems: 'center', gap: '2px' }}
          >
            <DragHandle />
            <div style={{ flex: 1 }}>
            <div 
              className="test-group-header" 
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                {/* Expand icon - always rendered for alignment, hidden when no steps */}
                {/* Matches step-expand-icon pattern: hover bg, focus ring, keyboard support */}
                <span
                  className="expand-icon"
                  onMouseDown={(e) => {
                    // Stop propagation at mousedown to prevent edit modal from opening
                    e.stopPropagation()
                  }}
                  onClick={(e) => {
                    e.stopPropagation()
                    if (item.testSteps?.length > 0) {
                      handleToggleTest(item.id)
                    }
                  }}
                  role="button"
                  aria-expanded={expandedTests.includes(item.id)}
                  aria-label={expandedTests.includes(item.id) ? 'Collapse test steps' : 'Expand test steps'}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      e.stopPropagation()
                      if (item.testSteps?.length > 0) {
                        handleToggleTest(item.id)
                      }
                    }
                  }}
                  style={{
                    userSelect: 'none',
                    visibility: item.testSteps?.length > 0 ? 'visible' : 'hidden'
                  }}
                >
                  {expandedTests.includes(item.id)
                    ? <ChevronDownIcon label="Collapse" size="medium" />
                    : <ChevronRightIcon label="Expand" size="medium" />
                  }
                </span>
              {/* Display test case key if available, otherwise fall back to number */}
              {item.testCaseKey ? (
                <span
                  className="test-case-key"
                  style={{
                    fontSize: '12px',
                    color: 'var(--ds-text-subtlest, #6B778C)',
                    fontFamily: 'monospace',
                    marginRight: '8px'
                  }}
                >
                  {item.testCaseKey}
                </span>
              ) : (
                <span className="test-number">{item.number}</span>
              )}
              <h3 className="test-title">{item.title}</h3>
              {/* Metadata indicators - comments, attachments, and description.
                  Rendered only when at least one indicator is present so the
                  wrapper doesn't leave an empty gap next to the title. */}
              {(item.commentCount > 0 ||
                item.attachmentCount > 0 ||
                (item.description && item.description.trim().length > 0)) && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginLeft: '8px' }}>
                  {item.commentCount > 0 && (
                    <Tooltip content={`${item.commentCount} comment(s)`}>
                      <span style={{ display: 'flex', alignItems: 'center', cursor: 'default', transform: 'scale(0.75)' }}>
                        <CommentIcon label="Comments" size="small" color={token('color.icon.subtle')} />
                      </span>
                    </Tooltip>
                  )}
                  {item.attachmentCount > 0 && (
                    <Tooltip content={`${item.attachmentCount} attachment(s)`}>
                      <span style={{ display: 'flex', alignItems: 'center', cursor: 'default', transform: 'scale(0.75)' }}>
                        <AttachmentIcon label="Attachments" size="small" color={token('color.icon.subtle')} />
                      </span>
                    </Tooltip>
                  )}
                  {/* Description indicator - shown only when the test case has a
                      non-empty description. trim() guards against whitespace-only
                      values that would otherwise trigger the icon. */}
                  {item.description && item.description.trim().length > 0 && (
                    <Tooltip content="Has description">
                      <span style={{ display: 'flex', alignItems: 'center', cursor: 'default', transform: 'scale(0.75)' }}>
                        <AlignTextLeftIcon label="Description" size="small" color={token('color.icon.subtle')} />
                      </span>
                    </Tooltip>
                  )}
                </div>
              )}
              </div>
              <Tooltip content="Add step">
                <Button
                  className="add-step-button"
                  appearance="subtle"
                  onClick={(e) => {
                    e.stopPropagation()
                    // If test case has no steps, expand it first
                    if (!item.testSteps || item.testSteps.length === 0) {
                      dispatch(toggleTest(item.id))
                    }
                    setAddingStepToTestId(item.id)
                  }}
                  style={{ marginLeft: '8px', opacity: 0 }}
                  tabIndex={-1}
                >
                  <AddIcon label="Add step" size="small" />
                </Button>
              </Tooltip>
              {/* Delete button - appears on hover */}
              <Tooltip content="Delete test case">
                <Button
                  className="row-delete-button"
                  appearance="subtle"
                  spacing="compact"
                  iconBefore={<TrashIcon label="Delete" size="small" />}
                  onClick={(e) => {
                    e.stopPropagation()
                    handleDeleteClick('testcase', item)
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                  style={{ opacity: 0, marginLeft: '4px' }}
                  tabIndex={-1}
                />
              </Tooltip>
            </div>
            </div>
          </div>
        ),
        runContents: isLibrary ? [] : testRuns.map(run => {
          const execution = getExecutionStatus(item.executions, run.id.toString())
          return (
            <ExecutionCell
              key={run.id}
              testCaseId={item.id}
              runId={run.id}
              execution={execution}
              isTestCase={true}
              onExecutionClick={handleExecutionClick}
            />
          )
        })
      })

      // Add individual test step rows if expanded
      if (expandedTests.includes(item.id) && item.testSteps) {
        const totalSteps = item.testSteps.length
        item.testSteps.forEach((step, stepIndex) => {
          // Mark the last step in this test case for group separator styling
          const isLastStep = stepIndex === totalSteps - 1

          // Check if step has content to show (test data or expected result)
          const hasTestData = step.testData && step.testData.trim() !== ''
          const hasExpected = step.expected && step.expected.trim() !== ''
          const hasContent = hasTestData || hasExpected

          // Check if this step's details are expanded
          const isStepExpanded = expandedSteps.includes(step.id)

          // Build tooltip content for hover preview (only when collapsed)
          const tooltipContent = !isStepExpanded && hasContent
            ? (hasTestData ? `Test Data: ${step.testData.substring(0, 50)}${step.testData.length > 50 ? '...' : ''}` : '')
              + (hasTestData && hasExpected ? ' | ' : '')
              + (hasExpected ? `Expected: ${step.expected.substring(0, 50)}${step.expected.length > 50 ? '...' : ''}` : '')
            : null

          tableRows.push({
            id: `step-${item.id}-${step.id}`,
            type: 'step',
            item: { testCase: item, step: step },
            isLastStep: isLastStep,
            draftingContent: (
              <div
                className={`test-step ${isStepExpanded ? 'expanded' : 'collapsed'}`}
                onMouseDown={(e) => {
                  // Don't open modal if clicking on execution columns
                  if (e.target.closest('.execution-column') || e.target.closest('.execution-cell')) {
                    return
                  }
                  // Don't open modal if clicking on expand icon, delete button, or drag handle
                  if (!e.target.closest('.drag-handle') &&
                      !e.target.closest('.step-expand-icon') &&
                      !e.target.closest('.row-delete-button')) {
                    e.stopPropagation()
                    handleMouseDown(e, { testCase: item, step: step }, true)
                  }
                }}
                onClick={(e) => {
                  // Prevent click event from bubbling when clicking drag handle
                  if (e.target.closest('.drag-handle')) {
                    e.stopPropagation()
                  }
                }}
                style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '2px' }}
              >
                <DragHandle />
                <div style={{ flex: 1 }}>
                  {/* Step header with expand/collapse control */}
                  {/* Using space-between layout to push delete button to far right, matching test case header pattern */}
                  <div className="step-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    {/* Left content wrapper - contains expand icon, number, title, and attachment indicator */}
                    <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                      {/* Expand/Collapse chevron - always rendered for alignment, hidden when step has no content */}
                      {/* Matches test case expand-icon pattern (visibility-toggle) so the flex layout is identical in both states; */}
                      {/* an empty spacer span produced subtly different layout when the step title wrapped to multiple lines */}
                      <span
                        className="step-expand-icon"
                        onMouseDown={(e) => {
                          // Stop propagation at mousedown to prevent edit modal from opening
                          e.stopPropagation()
                        }}
                        onClick={(e) => {
                          e.stopPropagation()
                          if (hasContent) {
                            toggleStepExpansion(step.id)
                          }
                        }}
                        role="button"
                        aria-expanded={hasContent ? isStepExpanded : undefined}
                        aria-label={isStepExpanded ? 'Collapse step details' : 'Expand step details'}
                        tabIndex={hasContent ? 0 : -1}
                        onKeyDown={(e) => {
                          if ((e.key === 'Enter' || e.key === ' ') && hasContent) {
                            e.preventDefault()
                            e.stopPropagation()
                            toggleStepExpansion(step.id)
                          }
                        }}
                        style={{
                          visibility: hasContent ? 'visible' : 'hidden'
                        }}
                      >
                        {isStepExpanded
                          ? <ChevronDownIcon label="Collapse" size="medium" />
                          : <ChevronRightIcon label="Expand" size="medium" />
                        }
                      </span>
                      <span className="step-number">{step.number}</span>
                      {/* Wrap title in Tooltip for hover preview when collapsed */}
                      <Tooltip content={tooltipContent} delay={500}>
                        <h4 className="step-title">{step.title}</h4>
                      </Tooltip>
                      {/* Metadata indicators - comments and attachments */}
                      {(step.commentCount > 0 || step.attachmentCount > 0) && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginLeft: '8px' }}>
                          {step.commentCount > 0 && (
                            <Tooltip content={`${step.commentCount} comment(s)`}>
                              <span style={{ display: 'flex', alignItems: 'center', cursor: 'default', transform: 'scale(0.75)' }}>
                                <CommentIcon label="Comments" size="small" color={token('color.icon.subtle')} />
                              </span>
                            </Tooltip>
                          )}
                          {step.attachmentCount > 0 && (
                            <Tooltip content={`${step.attachmentCount} attachment(s)`}>
                              <span style={{ display: 'flex', alignItems: 'center', cursor: 'default', transform: 'scale(0.75)' }}>
                                <AttachmentIcon label="Attachments" size="small" color={token('color.icon.subtle')} />
                              </span>
                            </Tooltip>
                          )}
                        </div>
                      )}
                    </div>
                    {/* Delete button - appears on hover, positioned at far right */}
                    <Tooltip content="Delete step">
                      <Button
                        className="row-delete-button"
                        appearance="subtle"
                        spacing="compact"
                        iconBefore={<TrashIcon label="Delete" size="small" />}
                        onClick={(e) => {
                          e.stopPropagation()
                          handleDeleteClick('step', { testCase: item, step: step })
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                        style={{ opacity: 0 }}
                        tabIndex={-1}
                      />
                    </Tooltip>
                  </div>

                  {/* Collapsible step content - shown/hidden based on expansion state */}
                  {hasContent && (
                    <div className={`step-content-wrapper ${isStepExpanded ? 'expanded' : 'collapsed'}`}>
                      {hasTestData && hasExpected ? (
                        // Both fields present: side-by-side layout
                        <div className="step-content-grid">
                          <div className="step-content-box">
                            <span className="step-label">Test Data</span>
                            <span className="step-text">{step.testData}</span>
                          </div>
                          <div className="step-content-box">
                            <span className="step-label">Expected</span>
                            <span className="step-text">{step.expected}</span>
                          </div>
                        </div>
                      ) : (
                        // Only one field present: full width
                        <div className="step-content-single">
                          {hasTestData && (
                            <div className="step-content-box">
                              <span className="step-label">Test Data</span>
                              <span className="step-text">{step.testData}</span>
                            </div>
                          )}
                          {hasExpected && (
                            <div className="step-content-box">
                              <span className="step-label">Expected</span>
                              <span className="step-text">{step.expected}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ),
            runContents: isLibrary ? [] : testRuns.map(run => {
              const execution = getExecutionStatus(step.executions, run.id.toString())
              return (
                <ExecutionCell
                  key={run.id}
                  testCaseId={item.id}
                  testStepId={step.id}
                  runId={run.id}
                  execution={execution}
                  isTestCase={false}
                  onExecutionClick={handleExecutionClick}
                />
              )
            })
          })
        })
      }

      // Add inline step input if this test case is being edited
      if (addingStepToTestId === item.id) {
        tableRows.push({
          id: `step-input-${item.id}`,
          draftingContent: (
            <div className="inline-create-row inline-create-step" ref={stepInputRowRef}>
              <TextField
                placeholder="Describe the step..."
                value={newStepText}
                onChange={(e) => setNewStepText(e.target.value)}
                onKeyDown={(e) => handleStepKeyPress(e, item.id)}
                onBlur={() => handleStepBlur(item.id)}
                autoFocus
                isCompact
              />
              <Button
                className="inline-create-button"
                appearance="primary"
                spacing="compact"
                onClick={() => handleCreateTestStep(item.id)}
                isDisabled={!newStepText.trim()}
                iconAfter={<EnterKeyIcon size="small" />}
              >
                Add
              </Button>
            </div>
          ),
          runContents: isLibrary ? [] : testRuns.map(run => (
            <div key={run.id}></div>
          ))
        })
      }
    }
  })

  // Note: Create row is now rendered outside the table as a sticky div
  // See the sticky-create-container div below the DndContext

  return (
    <div className="test-management-container">
      <HeaderSection
        onAddNewRun={handleAddNewRun}
        onOpenReport={handleOpenReport}
        testCases={testCases}
        testRuns={testRuns}
        isLibrary={isLibrary}
        selectedProject={selectedProject}
      />
      <TestCaseEditModal
        isOpen={isTestCaseModalOpen}
        onClose={handleCloseTestCaseModal}
        testCase={editingTestCase}
      />
      <TestStepEditModal
        isOpen={isTestStepModalOpen}
        onClose={handleCloseTestStepModal}
        testStep={editingTestStep}
      />
      <ExecutionDetailsModal
        isOpen={isExecutionDetailsModalOpen}
        onClose={handleCloseExecutionDetailsModal}
        execution={editingExecution}
      />
      {/* TEST SCENARIO EDIT MODAL */}
      {isTestScenarioModalOpen && editingTestScenario && (
        <TestScenarioEditModal
          isOpen={isTestScenarioModalOpen}
          onClose={handleCloseTestScenarioModal}
          testScenario={editingTestScenario}
          isLibrary={isLibrary}
        />
      )}
      {/* DELETE CONFIRMATION MODAL - Quick delete for test cases and steps */}
      <ConfirmDeleteModal
        isOpen={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
        title={deleteTarget?.type === 'testcase' ? 'Delete Test Case' : 'Delete Test Step'}
        itemName={deleteTarget?.title || 'item'}
        itemType="scenario"
      />
      {/* RUN-RELATED MODALS - Only shown in release mode, hidden for library templates */}
      {!isLibrary && (
        <>
          {/* NEW RUN MODAL - Creates test run with custom field values */}
          <NewRunModal
            isOpen={isNewRunModalOpen}
            onClose={handleCloseNewRunModal}
            testScenarioId={testScenario.testScenarioId}
            customFields={testRunCustomFields}
          />
          {/* CUSTOM FIELDS MODAL - Manage execution column custom fields */}
          <CustomFieldsModal
            isOpen={isCustomFieldsModalOpen}
            onClose={() => setIsCustomFieldsModalOpen(false)}
            customFields={testRunCustomFields}
            jiraProjectId={selectedProject?.value}
          />
          {/* EDIT RUN MODAL - Edit field values for an existing test run */}
          <EditRunModal
            isOpen={!!editingRun}
            onClose={() => {
              setEditingRun(null)
              setEditingRunUserData({})
            }}
            testScenarioId={testScenario.testScenarioId}
            run={editingRun}
            customFields={testRunCustomFields}
            preloadedUserData={editingRunUserData}
          />
          {/* RUN ACTION CONFIRMATION MODAL - Confirm delete/clear run operations */}
          <ConfirmRunActionModal
            isOpen={runActionModal.isOpen}
            onClose={closeRunActionModal}
            onConfirm={runActionModal.actionType === 'delete' ? confirmDeleteRun : confirmClearRun}
            actionType={runActionModal.actionType}
            runName={runActionModal.run?.[
              testRunCustomFields.find(f => f.fieldName === 'Run Name' || f.fieldName === 'Name')?.fieldName
            ] || ''}
            runNumber={runActionModal.runNumber}
          />
          {/* REPORT MODAL - View execution summary reports */}
          <ReportModal
            isOpen={isReportModalOpen}
            onClose={handleCloseReportModal}
            initialLevel="scenario"
            projectId={selectedProject?.value}
            releaseId={testScenario?.releaseId}
            scenarioId={testScenario?.testScenarioId || scenarioId}
          />
        </>
      )}
      {/* Scenario content wrapper - provides page-level padding around the content card */}
      <div className="scenario-content-wrapper">
        {/* Scenario content card - rounded container with card styling */}
        <div className="scenario-content-card">
          <div className="main-content">
            <div className="content-panels">
              <div className="execution-scroll-container" ref={scrollContainerRef}>
                <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDragEnd={handleDragEnd}
              onDragCancel={handleDragCancel}
            >
              <table className="test-table">
                <tbody>
                  <SortableContext
                    items={tableRows
                      .filter(row => row.id !== 'header') // Exclude header from sortable items (create row is now outside table)
                      .map(row => row.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    {tableRows.map((row) => (
                      <SortableRow
                        key={row.id}
                        id={row.id}
                        isInvalidDropZone={dragState.invalidDropZone === row.id}
                        isDraggingStep={dragState.draggedItemType === 'step'}
                        isLastStep={row.isLastStep || false}
                        isCollapsedTestcase={row.isCollapsedTestcase || false}
                        onMouseDown={(e) => {
                          // Don't open modal if clicking on execution columns
                          if (e.target.closest('.execution-column') || e.target.closest('.execution-cell')) {
                            return
                          }

                          if (row.type === 'testcase') {
                            if (!e.target.closest('.expand-icon') &&
                                !e.target.closest('.add-step-button') &&
                                !e.target.closest('.row-delete-button') &&
                                !e.target.closest('.drag-handle')) {
                              handleMouseDown(e, row.item, false)
                            }
                          } else if (row.type === 'step') {
                            if (!e.target.closest('.drag-handle') &&
                                !e.target.closest('.step-expand-icon') &&
                                !e.target.closest('.row-delete-button')) {
                              handleMouseDown(e, row.item, true)
                            }
                          }
                        }}
                      >
                        <td className={`drafting-column ${dragState.invalidDropZone === row.id ? 'invalid-drop-zone' : ''}`}>
                          <div className="drafting-cell">
                            {row.draftingContent}
                          </div>
                        </td>
                        {row.runContents.map((runContent, index) => (
                          <td key={index} className="execution-column">
                            <div className="execution-cell">
                              {runContent}
                            </div>
                          </td>
                        ))}
                        {/* Filler column to extend row borders to full container width */}
                        <td className="filler-column"></td>
                      </SortableRow>
                    ))}
                  </SortableContext>
                </tbody>
              </table>
                {/* DragOverlay renders a floating card preview while dragging */}
                <DragOverlay dropAnimation={null}>
                  {activeItem ? (
                    <div className={`drag-overlay-card ${activeItem.type === 'testcase' ? 'drag-overlay-testcase' : 'drag-overlay-step'}`}>
                      <DragHandlerIcon label="" size="small" />
                      <span className="drag-overlay-key">{activeItem.key}</span>
                      <span className="drag-overlay-title">{activeItem.title}</span>
                    </div>
                  ) : null}
                </DragOverlay>
                </DndContext>

                {/* Sticky Create Row - Outside table for reliable sticky positioning */}
                {/* Matches Jira's approach where the create action is not part of the scrollable table */}
                {(
                  <div className="sticky-create-container">
                    {isCreating ? (
                      <div className="inline-create-row">
                        <TextField
                          placeholder="What needs to be tested?"
                          value={newTestCaseName}
                          onChange={(e) => setNewTestCaseName(e.target.value)}
                          onKeyDown={handleKeyPress}
                          onBlur={handleTestCaseBlur}
                          autoFocus
                          isCompact
                        />
                        <Button
                          className="inline-create-button"
                          appearance="primary"
                          spacing="compact"
                          onClick={handleCreateTestCase}
                          isDisabled={!newTestCaseName.trim()}
                          iconAfter={<EnterKeyIcon size="small" />}
                        >
                          Create
                        </Button>
                      </div>
                    ) : (
                      <div
                        className="inline-create-trigger"
                        onClick={() => setIsCreating(true)}
                      >
                        <AddIcon label="Create" size="small" primaryColor="currentColor" />
                        <span className="inline-create-text">Create</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default App